import { z } from "zod";

// ============================================================================
// Enums
// ============================================================================

export const FieldTypeEnum = z.enum([
  "static",
  "dynamic",
  "attribute_summary_table",
  "sample_summary_table",
  "detail_testing_table",
  "citation_asset_sheet",
  "computed_table",
]);

// Field source types for static fields
// To add new source types (e.g., "procedure", "attribute"):
// 1. Add the new value to this enum array
// 2. Add it to SOURCES_BY_TEMPLATE_TYPE below. The backend requires a static
//    field's source to equal its template's type, so a second source for a type
//    needs a backend change first
// 3. Update FIELD_OPTIONS and SOURCE_OPTIONS in StaticFieldForm.tsx with the new source's available fields and label
// 4. Coordinate with backend to ensure the database supports the new enum value
export const FieldSourceEnum = z.enum([
  "control",
  "work_step",
  // Future source types can be added here
  // "procedure",
  // "attribute",
]);

// The kind of thing a template is built for
export const TemplateTypeEnum = z.enum(["control", "work_step"]);

// A backend from before template types sends none; every template it holds is
// a control template (the column's default), so the template pages keep working
// through a backend-only rollback.
const ResponseTemplateTypeSchema = TemplateTypeEnum.default("control");

export const ComputedFieldContextEnum = z.enum([
  "test_results",
  "control_documentation",
]);

export type FieldType = z.infer<typeof FieldTypeEnum>;
export type FieldSource = z.infer<typeof FieldSourceEnum>;
export type TemplateType = z.infer<typeof TemplateTypeEnum>;
export type ComputedFieldContext = z.infer<typeof ComputedFieldContextEnum>;

// A template's type decides which sources its fields may use. One source each
// today; the commented future sources above are why this stays a dropdown
// rather than being derived away. The backend enforces the same rule: it
// refuses a static field whose source isn't the template's type.
export const SOURCES_BY_TEMPLATE_TYPE: Record<TemplateType, FieldSource[]> = {
  control: ["control"],
  work_step: ["work_step"],
};

// ============================================================================
// Cell Address Schema
// ============================================================================

export const CellAddressSchema = z.object({
  row: z.number().int().min(1),
  column: z.string().regex(/^[A-Z]+$/),
});

export type CellAddress = z.infer<typeof CellAddressSchema>;

// ============================================================================
// Field Type Schemas
// ============================================================================

export const StaticFieldSchema = z.object({
  id: z.string().uuid(),
  sheetName: z.string().min(1),
  cellAddress: CellAddressSchema,
  source: FieldSourceEnum,
  sourceSelector: z.string().min(1),
});

export const ComputedFieldSchema = z.object({
  id: z.string().uuid(),
  sheetName: z.string().min(1),
  cellAddress: CellAddressSchema,
  context: ComputedFieldContextEnum,
  instructions: z.string().min(1),
});

export const AttributeSummaryTableSchema = z.object({
  sheetName: z.string().min(1),
  startRow: z.number().int().min(1),
  attributeNameColumn: z.string().regex(/^[A-Z]+$/),
  attributeOutcomeColumn: z.string().regex(/^[A-Z]+$/),
  attributeOutcomeDescriptionColumn: z
    .string()
    .regex(/^[A-Z]+$/)
    .nullable()
    .optional(),
  descriptionInstructions: z.string().nullable().optional(),
  // Evidence summary configuration (mutually exclusive)
  evidenceSummaryColumn: z
    .string()
    .regex(/^[A-Z]+$/)
    .nullable()
    .optional(),
  concatEvidenceWithDescription: z.boolean().default(false),
});

export const SampleSummaryTableSchema = z.object({
  sheetName: z.string().min(1),
  startRow: z.number().int().min(1),
  attributeHeaderRow: z.number().int().min(1),
  sampleNumberColumn: z.string().regex(/^[A-Z]+$/),
  outcomeStartColumn: z.string().regex(/^[A-Z]+$/),
});

// Hex color validation schema (6 chars, 0-9 A-F)
const HexColorSchema = z
  .string()
  .regex(/^[0-9A-Fa-f]{6}$/)
  .optional();

export const TableColorConfigSchema = z.object({
  divider: HexColorSchema,
  sampleHeader: HexColorSchema,
  testHeader: HexColorSchema,
  sampleColumnHeader: HexColorSchema,
  testColumnHeader: HexColorSchema,
  passFill: HexColorSchema,
  failFill: HexColorSchema,
});

export type TableColorConfig = z.infer<typeof TableColorConfigSchema>;

export const DetailTestingTableSchema = z.object({
  // Single-sheet mode: exact sheet name (XOR with sheetNamePrefix)
  sheetName: z.string().min(1).nullable().optional(),
  // Multi-sheet mode: prefix for generated sheet names (XOR with sheetName)
  sheetNamePrefix: z.string().min(1).nullable().optional(),
  sheetNameTemplate: z.string().default("{prefix}_{procedure_name}"),
  startRow: z.number().int().min(1).default(2),
  headerRow: z.number().int().min(1).default(1),
  columnStart: z
    .string()
    .regex(/^[A-Z]+$/)
    .default("A"),
  includeSampleAttributes: z.boolean().default(true),
  includeProcedureIds: z.array(z.string()).nullable().optional(),
  // Optional custom colors
  colors: TableColorConfigSchema.nullable().optional(),
});

export const CitationAssetSheetOrganizationEnum = z.enum([
  "by_sample",
  "by_eval_column",
  "by_attribute",
  "by_attribute_and_sample",
]);

export type CitationAssetSheetOrganization = z.infer<
  typeof CitationAssetSheetOrganizationEnum
>;

export const CitationAssetSheetSchema = z.object({
  sheetNamePrefix: z.string().min(1),
  sheetNameTemplate: z.string().default("{prefix}_{procedure_name}"),
  organization: CitationAssetSheetOrganizationEnum.default("by_sample"),
  startRow: z.number().int().min(1).default(1),
  imagesPerRow: z.number().int().min(1).default(1),
  includeAnnotations: z.boolean().default(true),
  includeNonAttributeTests: z.boolean().default(false),
  includeProcedureIds: z.array(z.string()).nullable().optional(),
  truncateEvalColumnStepsTaken: z.boolean().default(false),
});

// ============================================================================
// Computed Table Field Schemas (AI-generated tabular data)
// ============================================================================

export const ComputedTableColumnDataTypeEnum = z.enum([
  "string",
  "number",
  "boolean",
]);

export type ComputedTableColumnDataType = z.infer<
  typeof ComputedTableColumnDataTypeEnum
>;

export const ComputedTableColumnSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  dataType: ComputedTableColumnDataTypeEnum.default("string"),
  instructions: z.string().nullable().optional(),
  columnOrder: z.number().int().min(0),
});

export const ComputedTableFieldSchema = z.object({
  id: z.string().uuid(),
  sheetName: z.string().min(1),
  startRow: z.number().int().min(1).default(2),
  headerRow: z.number().int().min(1).default(1),
  columnStart: z
    .string()
    .regex(/^[A-Z]+$/)
    .default("A"),
  context: ComputedFieldContextEnum,
  instructions: z.string().min(1),
  columns: z.array(ComputedTableColumnSchema),
});

export type StaticField = z.infer<typeof StaticFieldSchema>;
export type ComputedField = z.infer<typeof ComputedFieldSchema>;
export type AttributeSummaryTable = z.infer<typeof AttributeSummaryTableSchema>;
export type SampleSummaryTable = z.infer<typeof SampleSummaryTableSchema>;
export type DetailTestingTable = z.infer<typeof DetailTestingTableSchema>;
export type CitationAssetSheet = z.infer<typeof CitationAssetSheetSchema>;
export type ComputedTableColumn = z.infer<typeof ComputedTableColumnSchema>;
export type ComputedTableField = z.infer<typeof ComputedTableFieldSchema>;

// ============================================================================
// Template Field (Discriminated Union)
// ============================================================================

export const TemplateFieldSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  fieldType: FieldTypeEnum,
  fieldOrder: z.number().int().min(0),
  staticField: StaticFieldSchema.nullable().optional(),
  computedField: ComputedFieldSchema.nullable().optional(),
  attributeSummaryTable: AttributeSummaryTableSchema.nullable().optional(),
  sampleSummaryTable: SampleSummaryTableSchema.nullable().optional(),
  detailTestingTable: DetailTestingTableSchema.nullable().optional(),
  citationAssetSheet: CitationAssetSheetSchema.nullable().optional(),
  computedTableField: ComputedTableFieldSchema.nullable().optional(),
});

export type TemplateField = z.infer<typeof TemplateFieldSchema>;

// Simplified version returned by GET endpoint (without metadata)
export const TemplateFieldResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  fieldType: FieldTypeEnum,
  fieldOrder: z.number().int().min(0).optional(),
  staticField: StaticFieldSchema.nullable().optional(),
  computedField: ComputedFieldSchema.nullable().optional(),
  attributeSummaryTable: AttributeSummaryTableSchema.nullable().optional(),
  sampleSummaryTable: SampleSummaryTableSchema.nullable().optional(),
  detailTestingTable: DetailTestingTableSchema.nullable().optional(),
  citationAssetSheet: CitationAssetSheetSchema.nullable().optional(),
  computedTableField: ComputedTableFieldSchema.nullable().optional(),
});

export type TemplateFieldResponse = z.infer<typeof TemplateFieldResponseSchema>;

// ============================================================================
// Template Schema
// ============================================================================

export const WorkPaperTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  type: ResponseTemplateTypeSchema,
  isBaseTemplate: z.boolean(),
  workspaceId: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const WorkPaperTemplateWithFieldsSchema = WorkPaperTemplateSchema.extend(
  {
    fields: z.array(TemplateFieldSchema),
  },
);

export type WorkPaperTemplate = z.infer<typeof WorkPaperTemplateSchema>;
export type WorkPaperTemplateWithFields = z.infer<
  typeof WorkPaperTemplateWithFieldsSchema
>;

// Simplified version returned by GET endpoint (without metadata)
export const WorkPaperTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  type: ResponseTemplateTypeSchema,
  isBaseTemplate: z.boolean().optional(),
  workspaceId: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});

export const WorkPaperTemplateWithFieldsResponseSchema =
  WorkPaperTemplateResponseSchema.extend({
    fields: z.array(TemplateFieldResponseSchema),
  });

export type WorkPaperTemplateResponse = z.infer<
  typeof WorkPaperTemplateResponseSchema
>;
export type WorkPaperTemplateWithFieldsResponse = z.infer<
  typeof WorkPaperTemplateWithFieldsResponseSchema
>;

// ============================================================================
// API Request Schemas
// ============================================================================

export const CreateTemplateRequestSchema = z.object({
  name: z.string().min(1).max(255),
  type: TemplateTypeEnum,
});

// A template's type is fixed at creation, so an update never sends one.
export const UpdateTemplateRequestSchema = CreateTemplateRequestSchema.pick({
  name: true,
});

export type CreateTemplateRequest = z.infer<typeof CreateTemplateRequestSchema>;
export type UpdateTemplateRequest = z.infer<typeof UpdateTemplateRequestSchema>;

// Field Creation Requests (discriminated union)
export const CreateStaticFieldRequestSchema = z.object({
  name: z.string().min(1).max(255),
  fieldType: z.literal("static"),
  staticField: z.object({
    sheetName: z.string().min(1),
    cellRow: z.number().int().min(1),
    cellColumn: z.string().regex(/^[A-Z]+$/),
    source: FieldSourceEnum,
    sourceSelector: z.string().min(1),
  }),
});

export const CreateComputedFieldRequestSchema = z.object({
  name: z.string().min(1).max(255),
  fieldType: z.literal("dynamic"),
  computedField: z.object({
    sheetName: z.string().min(1),
    cellRow: z.number().int().min(1),
    cellColumn: z.string().regex(/^[A-Z]+$/),
    context: ComputedFieldContextEnum,
    instructions: z.string().min(1),
  }),
});

export const CreateAttributeTableRequestSchema = z.object({
  name: z.string().min(1).max(255),
  fieldType: z.literal("attribute_summary_table"),
  attributeSummaryTable: z.object({
    sheetName: z.string().min(1),
    startRow: z.number().int().min(1),
    attributeNameColumn: z.string().regex(/^[A-Z]+$/),
    attributeOutcomeColumn: z.string().regex(/^[A-Z]+$/),
    attributeOutcomeDescriptionColumn: z
      .string()
      .regex(/^[A-Z]+$/)
      .nullable()
      .optional(),
    descriptionInstructions: z.string().nullable().optional(),
    // Evidence summary configuration (mutually exclusive)
    evidenceSummaryColumn: z
      .string()
      .regex(/^[A-Z]+$/)
      .nullable()
      .optional(),
    concatEvidenceWithDescription: z.boolean().optional(),
  }),
});

export const CreateSampleTableRequestSchema = z.object({
  name: z.string().min(1).max(255),
  fieldType: z.literal("sample_summary_table"),
  sampleSummaryTable: z.object({
    sheetName: z.string().min(1),
    startRow: z.number().int().min(1),
    attributeHeaderRow: z.number().int().min(1),
    sampleNumberColumn: z.string().regex(/^[A-Z]+$/),
    outcomeStartColumn: z.string().regex(/^[A-Z]+$/),
  }),
});

export const CreateDetailTestingTableRequestSchema = z.object({
  name: z.string().min(1).max(255),
  fieldType: z.literal("detail_testing_table"),
  detailTestingTable: z.object({
    // XOR: exactly one of sheetName or sheetNamePrefix must be provided
    sheetName: z.string().min(1).optional(),
    sheetNamePrefix: z.string().min(1).optional(),
    sheetNameTemplate: z.string().optional(),
    startRow: z.number().int().min(1).optional(),
    headerRow: z.number().int().min(1).optional(),
    columnStart: z
      .string()
      .regex(/^[A-Z]+$/)
      .optional(),
    includeSampleAttributes: z.boolean().optional(),
    includeProcedureIds: z.array(z.string()).nullable().optional(),
    colors: TableColorConfigSchema.nullable().optional(),
  }),
});

export const CreateCitationAssetSheetRequestSchema = z.object({
  name: z.string().min(1).max(255),
  fieldType: z.literal("citation_asset_sheet"),
  citationAssetSheet: z.object({
    sheetNamePrefix: z.string().min(1),
    sheetNameTemplate: z.string().optional(),
    organization: CitationAssetSheetOrganizationEnum.optional(),
    startRow: z.number().int().min(1).optional(),
    imagesPerRow: z.number().int().min(1).optional(),
    includeAnnotations: z.boolean().optional(),
    includeNonAttributeTests: z.boolean().optional(),
    includeProcedureIds: z.array(z.string()).nullable().optional(),
    truncateEvalColumnStepsTaken: z.boolean().optional(),
  }),
});

export const CreateComputedTableColumnRequestSchema = z.object({
  name: z.string().min(1),
  dataType: ComputedTableColumnDataTypeEnum.optional(),
  instructions: z.string().nullable().optional(),
  columnOrder: z.number().int().min(0),
});

export const CreateComputedTableFieldRequestSchema = z.object({
  name: z.string().min(1).max(255),
  fieldType: z.literal("computed_table"),
  computedTableField: z.object({
    sheetName: z.string().min(1),
    startRow: z.number().int().min(1).optional(),
    headerRow: z.number().int().min(1).optional(),
    columnStart: z
      .string()
      .regex(/^[A-Z]+$/)
      .optional(),
    context: ComputedFieldContextEnum,
    instructions: z.string().min(1),
    columns: z.array(CreateComputedTableColumnRequestSchema).min(1),
  }),
});

export const CreateFieldRequestSchema = z.discriminatedUnion("fieldType", [
  CreateStaticFieldRequestSchema,
  CreateComputedFieldRequestSchema,
  CreateAttributeTableRequestSchema,
  CreateSampleTableRequestSchema,
  CreateDetailTestingTableRequestSchema,
  CreateCitationAssetSheetRequestSchema,
  CreateComputedTableFieldRequestSchema,
]);

export const UpdateFieldRequestSchema = CreateFieldRequestSchema;

export type CreateFieldRequest = z.infer<typeof CreateFieldRequestSchema>;
export type UpdateFieldRequest = z.infer<typeof UpdateFieldRequestSchema>;

// Reorder Fields Request
export const ReorderFieldsRequestSchema = z.object({
  fieldUpdates: z
    .array(
      z.object({
        fieldId: z.string().uuid(),
        fieldOrder: z.number().int().min(0),
      }),
    )
    .min(1),
});

export type ReorderFieldsRequest = z.infer<typeof ReorderFieldsRequestSchema>;

// ============================================================================
// API Response Schemas
// ============================================================================

export const ListTemplatesResponseSchema = z.object({
  templates: z.array(WorkPaperTemplateSchema),
});

export const GetTemplateResponseSchema = z.object({
  template: WorkPaperTemplateWithFieldsResponseSchema,
});

export const CreateTemplateResponseSchema = z.object({
  templateId: z.string().uuid(),
  message: z.string(),
});

export const DuplicateTemplateResponseSchema = CreateTemplateResponseSchema;

export const CreateFieldResponseSchema = z.object({
  fieldId: z.string().uuid(),
  fieldOrder: z.number().int(),
  message: z.string(),
});

export const MessageResponseSchema = z.object({
  message: z.string(),
});

export type ListTemplatesResponse = z.infer<typeof ListTemplatesResponseSchema>;
export type GetTemplateResponse = z.infer<typeof GetTemplateResponseSchema>;
export type CreateTemplateResponse = z.infer<
  typeof CreateTemplateResponseSchema
>;
export type DuplicateTemplateResponse = z.infer<
  typeof DuplicateTemplateResponseSchema
>;
export type CreateFieldResponse = z.infer<typeof CreateFieldResponseSchema>;
export type MessageResponse = z.infer<typeof MessageResponseSchema>;

// ============================================================================
// Draft State Types (for local editing)
// ============================================================================

export interface DraftTemplateField extends Omit<TemplateField, "id"> {
  id: string; // Can be temporary ID like "temp-1"
  isNew?: boolean;
  isModified?: boolean;
  isDeleted?: boolean;
  isReordered?: boolean;
}

export interface DraftTemplate {
  id: string | null; // null for new templates
  name: string;
  type: TemplateType | null; // null until a new template's type is chosen
  fields: DraftTemplateField[];
  isBaseTemplate: boolean;
  workspaceId: string | null;
  isModified: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert column number (0-indexed) to Excel column letter
 * 0 -> A, 1 -> B, 25 -> Z, 26 -> AA, etc.
 */
export function columnNumberToLetter(num: number): string {
  let column = "";
  let n = num;
  while (n >= 0) {
    column = String.fromCharCode((n % 26) + 65) + column;
    n = Math.floor(n / 26) - 1;
  }
  return column;
}

/**
 * Convert Excel column letter to number (0-indexed)
 * A -> 0, B -> 1, Z -> 25, AA -> 26, etc.
 */
export function columnLetterToNumber(letter: string): number {
  let column = 0;
  for (let i = 0; i < letter.length; i++) {
    column = column * 26 + (letter.charCodeAt(i) - 64);
  }
  return column - 1;
}

/**
 * Get a human-readable field type label
 */
export function getFieldTypeLabel(fieldType: FieldType): string {
  switch (fieldType) {
    case "static":
      return "Static Field";
    case "dynamic":
      return "AI-Generated Field";
    case "attribute_summary_table":
      return "Attribute Summary Table";
    case "sample_summary_table":
      return "Sample Summary Table";
    case "detail_testing_table":
      return "Detail Testing Table";
    case "citation_asset_sheet":
      return "Citation Asset Sheet";
    case "computed_table":
      return "AI-Generated Table";
  }
}

/**
 * Get a human-readable template type label
 */
export function getTemplateTypeLabel(type: TemplateType): string {
  switch (type) {
    case "control":
      return "Control";
    case "work_step":
      return "Work Step";
  }
}

/**
 * Get a brief description of a field for display
 */
export function getFieldDescription(
  field: TemplateField | TemplateFieldResponse,
): string {
  switch (field.fieldType) {
    case "static":
      if (field.staticField) {
        return `${field.staticField.sheetName}!${field.staticField.cellAddress.column}${field.staticField.cellAddress.row} - ${field.staticField.sourceSelector}`;
      }
      return "Static field";
    case "dynamic":
      if (field.computedField) {
        return `${field.computedField.sheetName}!${field.computedField.cellAddress.column}${field.computedField.cellAddress.row} - AI Generated`;
      }
      return "AI-generated field";
    case "attribute_summary_table":
      if (field.attributeSummaryTable) {
        return `${field.attributeSummaryTable.sheetName} - Row ${field.attributeSummaryTable.startRow}+`;
      }
      return "Attribute table";
    case "sample_summary_table":
      if (field.sampleSummaryTable) {
        return `${field.sampleSummaryTable.sheetName} - Row ${field.sampleSummaryTable.startRow}+`;
      }
      return "Sample table";
    case "detail_testing_table":
      if (field.detailTestingTable) {
        if (field.detailTestingTable.sheetName) {
          return `${field.detailTestingTable.sheetName} - Single sheet testing table`;
        }
        return `${field.detailTestingTable.sheetNamePrefix}* - Testing tables per procedure`;
      }
      return "Detail testing table";
    case "citation_asset_sheet":
      if (field.citationAssetSheet) {
        return `${field.citationAssetSheet.sheetNamePrefix}* - Citation assets per procedure`;
      }
      return "Citation asset sheet";
    case "computed_table":
      if (field.computedTableField) {
        const columnCount = field.computedTableField.columns.length;
        return `${field.computedTableField.sheetName} - AI-generated table (${columnCount} column${columnCount !== 1 ? "s" : ""})`;
      }
      return "AI-generated table";
  }
}

// ============================================================================
// Template Field State (complete state for a session × template)
// ============================================================================

// Inline citation schema for evidence summaries (simpler than full Citation)
// Matches backend InlineCitationResponse
export const InlineCitationSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string(),
  encodedCitationString: z.string(),
  shortSummary: z.string().nullable().optional(),
  outcomeSpanStart: z.number().nullable().optional(),
  outcomeSpanEnd: z.number().nullable().optional(),
  citationOrder: z.number(),
});

export type InlineCitation = z.infer<typeof InlineCitationSchema>;

export const AttributeTableRowSchema = z.object({
  attributeId: z.string().uuid(),
  attributeOutcome: z.enum(["pass", "fail", "n/a", "not run"]),
  attributeOutcomeDescription: z.string().nullable(),
  // Evidence summary with inline citations (new fields)
  evidenceSummary: z.string().nullable().optional(),
  inlineCitations: z.array(InlineCitationSchema).optional(),
});

export const AttributeTableValueSchema = z.object({
  rows: z.array(AttributeTableRowSchema),
});

export const SampleTableAttributeResultSchema = z.object({
  attributeId: z.string().uuid(),
  outcome: z.string(),
});

export const SampleTableRowSchema = z.object({
  sampleId: z.string().uuid(),
  attributeResults: z.array(SampleTableAttributeResultSchema),
});

export const SampleTableValueSchema = z.object({
  rows: z.array(SampleTableRowSchema),
});

export const DetailTestingTableValueSchema = z.object({
  // These are generated dynamically per procedure, so we just track readiness
  isGenerated: z.boolean().default(false),
});

export const CitationAssetSheetValueSchema = z.object({
  // These are generated dynamically per procedure, so we just track readiness
  isGenerated: z.boolean().default(false),
});

// Computed Table Value - AI-generated tabular data
export const ComputedTableRowValueSchema = z.object({
  values: z.record(
    z.string(),
    z.union([z.string(), z.number(), z.boolean(), z.null()]),
  ),
});

export const ComputedTableValueSchema = z.object({
  rows: z.array(ComputedTableRowValueSchema),
});

export const FieldStateSchema = z.object({
  fieldId: z.string().uuid(),
  fieldType: FieldTypeEnum,
  staticValue: z.string().nullable(),
  computedValue: z.string().nullable(),
  attributeTableValue: AttributeTableValueSchema.nullable(),
  sampleTableValue: SampleTableValueSchema.nullable(),
  detailTestingTableValue: DetailTestingTableValueSchema.nullable().optional(),
  citationAssetSheetValue: CitationAssetSheetValueSchema.nullable().optional(),
  computedTableValue: ComputedTableValueSchema.nullable().optional(),
});

export const GetTemplateFieldStateResponseSchema = z.object({
  templateId: z.string().uuid(),
  sessionId: z.string().uuid(),
  fields: z.array(FieldStateSchema),
});

export type AttributeTableRow = z.infer<typeof AttributeTableRowSchema>;
export type AttributeTableValue = z.infer<typeof AttributeTableValueSchema>;
export type SampleTableAttributeResult = z.infer<
  typeof SampleTableAttributeResultSchema
>;
export type SampleTableRow = z.infer<typeof SampleTableRowSchema>;
export type SampleTableValue = z.infer<typeof SampleTableValueSchema>;
export type DetailTestingTableValue = z.infer<
  typeof DetailTestingTableValueSchema
>;
export type CitationAssetSheetValue = z.infer<
  typeof CitationAssetSheetValueSchema
>;
export type ComputedTableRowValue = z.infer<typeof ComputedTableRowValueSchema>;
export type ComputedTableValue = z.infer<typeof ComputedTableValueSchema>;
export type FieldState = z.infer<typeof FieldStateSchema>;
export type GetTemplateFieldStateResponse = z.infer<
  typeof GetTemplateFieldStateResponseSchema
>;

// ============================================================================
// Generated Field Values (for template + session combinations)
// ============================================================================

export const GeneratedFieldValueSchema = z.object({
  templateId: z.string().uuid(),
  sessionId: z.string().uuid(),
  fieldId: z.string(),
  version: z.number().int().min(1),
  value: z.unknown(), // JSONB field - can be string, number, object, array
  generatedAt: z.string().datetime(),
});

export type GeneratedFieldValue = z.infer<typeof GeneratedFieldValueSchema>;

export const GetGeneratedValuesResponseSchema = z.object({
  templateId: z.string().uuid(),
  sessionId: z.string().uuid(),
  values: z.array(GeneratedFieldValueSchema),
});

export type GetGeneratedValuesResponse = z.infer<
  typeof GetGeneratedValuesResponseSchema
>;

// ============================================================================
// Update Generated Value Schemas
// ============================================================================

export const UpdateGeneratedValueRequestSchema = z.object({
  value: z.string(),
});

export const UpdateGeneratedValueResponseSchema = z.object({
  fieldId: z.string().uuid(),
  value: z.string(),
  message: z.string(),
});

export type UpdateGeneratedValueRequest = z.infer<
  typeof UpdateGeneratedValueRequestSchema
>;
export type UpdateGeneratedValueResponse = z.infer<
  typeof UpdateGeneratedValueResponseSchema
>;
